import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { buildQueryset } from "@/utils/queryset";
import { getPhaseSignature } from "@/store/utils/sampleUtils";
import { cookRecordBookRecipe } from "@/store/utils/recordbooks";
import dayjs from "dayjs";
import pick from "lodash/pick";
import { useProjectsStore } from "@/store/modules/projects";
import { sortByLabel } from "@/utils/helpers";
import RecordBookClient from "@/api/clients/recordBookClient";

const { innerFetch, innerPartialUpdate, innerCreate } = crudGenerator(
  db.models.RecordBook
);

export const RECORD_BOOK_EDITABLE_FIELDS = [
  "label",
  "recordForm",
  "finished",
  "humidity",
];

const RECORD_BOOK_ORDER = [
  "environmental_condition",
  "speed",
  "analyser",
  "nozzle_diameter",
  "manual_sampling",
  "humidity",
];

const RECORD_BOOK_PARENT_FIELDS = {
  environmental_condition: "environmentalCondition",
  speed: "speed",
  humidity: "humidity",
  nozzle_diameter: "nozzleDiameter",
  manual_sampling: "manual",
};

const indexByUuid = async (table, uuids) => {
  const rows = await table
    .where("uuid")
    .anyOf([...new Set(uuids)])
    .toArray();
  return new Map(rows.map((row) => [row.uuid, row]));
};

const setSortingRelations = async (recordBooks) => {
  const installationByUuid = await indexByUuid(
    db.installations,
    recordBooks.map(({ installation }) => installation).filter(Boolean)
  );
  const environmentByUuid = await indexByUuid(
    db.recordBooks,
    recordBooks
      .map(({ environmentalCondition }) => environmentalCondition)
      .filter(Boolean)
  );
  for (const recordBook of recordBooks) {
    recordBook.installationObj = installationByUuid.get(
      recordBook.installation
    );
    recordBook.environmentalConditionObj = environmentByUuid.get(
      recordBook.environmentalCondition
    );
    recordBook.setDate();
  }
};

const getInterventionDate = (recordBook) =>
  recordBook.type === "environmental_condition"
    ? recordBook?.recordForm?.interventionDate
    : recordBook.environmentalConditionObj?.recordForm?.interventionDate;

export const useRecordBooksStore = defineStore("recordBooks", {
  state: () => ({
    recordBookIsLoading: false,
    recordBookIsFetching: false,
    recordBookList: [],
    totalRecordBooks: 0,
  }),
  actions: {
    async setCurrentRecordBook(uuid) {
      while (this.recordBookIsFetching)
        await new Promise((r) => setTimeout(r, 200));
      this.recordBookIsFetching = true;

      try {
        const recordBook = await db.recordBooks.get({ uuid });
        if (!recordBook) return null;
        await recordBook.setRelationships();
        return recordBook;
      } finally {
        this.recordBookIsFetching = false;
      }
    },

    async fetchRecordBooks(projectUuid) {
      const showSkeleton = !this.recordBookList.length;
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.recordBookIsFetching = isLoading;
          if (showSkeleton) this.recordBookIsLoading = isLoading;
        }
      );
    },

    async getRecordBook(recordBookUuid) {
      if (!recordBookUuid) return null;
      return db.recordBooks.get({ uuid: recordBookUuid });
    },

    async recordBookExistForInstallation(installationUuid) {
      const count = await db.recordBooks
        .where({
          project: useProjectsStore().currentProjectUuid,
          installation: installationUuid,
        })
        .filter(({ isArchived }) => !isArchived)
        .count();
      return count > 0;
    },

    async getRecorkBooksForInstallation({ projectUuid, installationUuid }) {
      const recordBooks = await db.recordBooks
        .where({ project: projectUuid, installation: installationUuid })
        .filter(({ isArchived }) => !isArchived)
        .toArray();

      for (const recordBook of recordBooks) {
        await recordBook.setRelationships();
      }

      return sortByLabel(recordBooks);
    },

    async createRecordBook({ projectUuid, form, sourceToDuplicate = null }) {
      const isOfflineState = useProjectsStore().isOfflineState;
      if (form.phase) {
        form.phaseSignature = await getPhaseSignature(form.phase);
      }
      const recordBookForm = {
        project: projectUuid,
        ...form,
        ...cookRecordBookRecipe(form.type, sourceToDuplicate),
      };
      const recordBook = await innerCreate(recordBookForm, isOfflineState);
      await db.models.RecordBook.createOrUpdate(recordBookForm);

      if (recordBookForm.type === "manual_sampling") {
        try {
          await recordBook.setFilterTemperature();
        } catch (e) {
          console.error("Failed Set FilterTempleratur", e);
        }
        try {
          await recordBook.setDurations();
        } catch (e) {
          console.error("Failed setDurations", e);
        }
        await innerPartialUpdate(
          { uuid: recordBook.uuid, recordForm: recordBook.recordForm },
          isOfflineState
        );
      }
      this.recordBookList = [recordBook, ...this.recordBookList];
      return recordBook;
    },

    async archiveRecordBooksByPhases({ phaseUuids, detachPhase }) {
      const uuids = Array.isArray(phaseUuids) ? phaseUuids : [phaseUuids];
      const changes = detachPhase
        ? { isArchived: true, phase: null }
        : { isArchived: true };
      await db.recordBooks.where("phase").anyOf(uuids).modify(changes);
      this.recordBookList = this.recordBookList.map((recordBook) =>
        uuids.includes(recordBook.phase)
          ? Object.assign(recordBook, changes)
          : recordBook
      );
    },

    async patchRecordBook(uuid, changes) {
      return innerPartialUpdate(
        { uuid, ...changes },
        useProjectsStore().isOfflineState
      );
    },

    async saveRecordBookEdits(recordBook) {
      return this.patchRecordBook(
        recordBook.uuid,
        pick(recordBook, RECORD_BOOK_EDITABLE_FIELDS)
      );
    },

    async setRecordBooksArchived(uuids, isArchived) {
      const isOfflineState = useProjectsStore().isOfflineState;
      if (!isOfflineState)
        await RecordBookClient.bulkArchive(uuids, isArchived);
      const changes = isOfflineState
        ? { isArchived, isUpdatedOffline: true }
        : { isArchived };
      await db.recordBooks.where("uuid").anyOf(uuids).modify(changes);
      this.recordBookList = this.recordBookList.map((recordBook) =>
        uuids.includes(recordBook.uuid)
          ? Object.assign(recordBook, changes)
          : recordBook
      );
    },

    async getDependentRecordBooks(recordBook) {
      const candidates = await db.recordBooks
        .where({ project: recordBook.project })
        .filter(
          ({ uuid, isArchived }) => !isArchived && uuid !== recordBook.uuid
        )
        .toArray();
      const dependents = new Map();
      const parents = [recordBook];
      while (parents.length) {
        const parent = parents.shift();
        const parentField = RECORD_BOOK_PARENT_FIELDS[parent.type];
        if (!parentField) continue;
        for (const book of candidates) {
          if (book[parentField] !== parent.uuid || dependents.has(book.uuid))
            continue;
          dependents.set(book.uuid, book);
          parents.push(book);
        }
      }
      return [...dependents.values()];
    },

    async getRecordBookForInstallationByType({ installationUuid, type }) {
      return db.recordBooks
        .where({ installation: installationUuid, type })
        .filter(({ isArchived }) => !isArchived)
        .toArray();
    },

    async filterRecordBooks({
      queryAsObject,
      ordering,
      filterMapping,
      filterByDate = "",
      filterByInstallation = [],
    }) {
      while (this.recordBookIsFetching)
        await new Promise((r) => setTimeout(r, 200));
      const showSkeleton = !this.recordBookList.length;
      this.recordBookIsFetching = true;
      if (showSkeleton) this.recordBookIsLoading = true;
      try {
        const postFetchSorting = true;
        // Because RecordBook sorting request are weird, we cannot use the generic QuerySet methods. They use OrderBy which is not suitable for our use cases
        const { queryset } = await buildQueryset(
          db.recordBooks,
          queryAsObject,
          "",
          filterMapping,
          postFetchSorting
        );
        let recordBooks = await queryset.toArray();

        const orderDesc = ordering[0] === "-" ? -1 : 1;
        await setSortingRelations(recordBooks);

        // le libellé sert d'ordre par défaut et départage les autres tris
        recordBooks = sortByLabel(recordBooks);
        if (ordering.includes("label") && orderDesc === -1)
          recordBooks.reverse();

        // sorting intervention date
        if (ordering.includes("interventionDate")) {
          recordBooks.sort((a, b) => {
            const dateA = dayjs(getInterventionDate(a), "YYYY-MM-DD");
            const dateB = dayjs(getInterventionDate(b), "YYYY-MM-DD");
            if (dateA.isSame(dateB)) return 0;
            return dateA < dateB ? -1 * orderDesc : 1 * orderDesc;
          });
        }

        // sorting updated date
        if (ordering.includes("updatedAt")) {
          recordBooks.sort((a, b) => {
            if (dayjs(a.updatedAt).isSame(dayjs(b.updatedAt))) return 0;
            return dayjs(a.updatedAt) < dayjs(b.updatedAt)
              ? -1 * orderDesc
              : 1 * orderDesc;
          });
        }

        // sorting installation Name
        if (ordering.includes("installation")) {
          recordBooks.sort((a, b) => {
            const installationA = a.installationObj
              ? a.installationObj.name
              : null;
            const installationB = b.installationObj
              ? b.installationObj.name
              : null;
            const alphaNameA = installationA
              ? installationA.replace(/\d/g, "").toLocaleLowerCase()
              : null;
            const alphaNameB = installationB
              ? installationB.replace(/\d/g, "").toLocaleLowerCase()
              : null;
            const numA = installationA ? Number.parseInt(installationA) : null;
            const numB = installationB ? Number.parseInt(installationB) : null;

            if (
              (!installationA && installationB) ||
              (alphaNameA > alphaNameB && !numA && !numB) ||
              (alphaNameA === alphaNameB && numA && numB && numA < numB)
            )
              return 1 * orderDesc;
            if (
              (installationA && !installationB) ||
              (alphaNameA < alphaNameB && !numA && !numB) ||
              (alphaNameA === alphaNameB && numA && numB && numA > numB)
            )
              return -1 * orderDesc;
            return 0;
          });
        }

        // sorting type
        if (ordering.includes("type")) {
          recordBooks.sort((a, b) => {
            const indexA = RECORD_BOOK_ORDER.indexOf(a.type);
            const indexB = RECORD_BOOK_ORDER.indexOf(b.type);
            if (indexA === indexB) return 0;
            return indexA < indexB ? 1 * orderDesc : -1 * orderDesc;
          });
        }

        // Sorting archibed record book at the end
        recordBooks.sort((a, b) => {
          if (a.isArchived === b.isArchived) return 0;
          return a.isArchived ? 1 : -1;
        });

        if (filterByDate !== "") {
          recordBooks = recordBooks.filter(({ date }) =>
            date?.includes(filterByDate)
          );
        }

        if (filterByInstallation.length) {
          recordBooks = recordBooks.filter(({ installation }) =>
            filterByInstallation.includes(installation)
          );
        }

        const offset = queryAsObject.page_size * (queryAsObject.page - 1);
        const size = offset + queryAsObject.page_size;

        const page = recordBooks.slice(offset, size);
        for (const recordBook of page) {
          await recordBook.setRelationships();
        }
        this.recordBookList = page;
        this.totalRecordBooks = recordBooks.length;
      } finally {
        this.recordBookIsFetching = false;
        if (showSkeleton) this.recordBookIsLoading = false;
      }
    },
  },
});
